export const StaticTV = {
  mounted() {
    const canvas = document.getElementById("tv-canvas");

    const gl = canvas.getContext("webgl", {
      alpha: true,
      premultipliedAlpha: false,
      preserveDrawingBuffer: true,
    });

    if (!gl) {
      console.error("WebGL not supported");
      return;
    }

    const vertexShaderSource = `
      attribute vec2 a_position;
      attribute vec2 a_texCoord;
      varying vec2 v_texCoord;
      void main() {
        gl_Position = vec4(a_position, 0, 1);
        v_texCoord = a_texCoord;
      }
    `;

    const fragmentShaderSource = `
      precision highp float;
      uniform vec2 u_resolution;
      uniform float u_time;
      varying vec2 v_texCoord;

      float noise(vec2 co, float time) {
        vec2 seed1 = co * 12.9898 + vec2(time * 23.233, -time * 9.78);
        vec2 seed2 = co * 43.5453 + vec2(-time * 5.988, time * 7.233);
        vec2 seed3 = co * 73.2349 + vec2(time * 12.43, -time * 15.2);

        float r1 = fract(sin(dot(seed1, vec2(12.9898, 78.233))) * 43758.5453);
        float r2 = fract(sin(dot(seed2, vec2(39.346, 11.135))) * 22693.3021);
        float r3 = fract(sin(dot(seed3, vec2(73.156, 52.235))) * 15473.5219);

        return fract(r1 + r2 + r3);
      }

      void main() {
        vec2 uv = v_texCoord;
        vec2 center = vec2(0.5, 0.5);

        // Fisheye effect
        vec2 dir = uv - center;
        float dist = length(dir);
        float distortion = 0.7;
        float factor = 1.0 + distortion * dist * dist;
        vec2 distorted = center + dir * factor;

        // Ensure distorted coordinates stay within bounds
        if (distorted.x < 0.0 || distorted.x > 1.0 || distorted.y < 0.0 || distorted.y > 1.0) {
          gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
          return;
        }

        // Add corner radius
        float cornerRadiusX = 0.05;  // X-axis corner radius
        float cornerRadiusY = 0.07; // Y-axis corner radius

        // Calculate distance from edges
        vec2 fromCenter = abs(distorted - vec2(0.5));
        vec2 edgeDist = fromCenter - vec2(0.5 - cornerRadiusX, 0.5 - cornerRadiusY);

        // Check if in corner region
        if (edgeDist.x > 0.0 && edgeDist.y > 0.0) {
          float cornerDist = length(vec2(edgeDist.x / cornerRadiusX, edgeDist.y / cornerRadiusY));
          if (cornerDist > 1.0) {
             gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
             return;
          }
        }

        // Check if outside the edges
        else if (fromCenter.x > 0.5 || fromCenter.y > 0.5) {
          gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
          return;
        }

        vec2 pixelCoord = gl_FragCoord.xy;

        // Add nosie and convert to pure black or white
        float noise = noise(pixelCoord, u_time);
        float staticNoise = step(0.5, noise);

        // Add scan lines
        float scanLine = sin(distorted.y * 800.0) * 0.1 + 0.95;

        // Add vignette
        float vignette = 1.0 - smoothstep(0.0, 0.8, dist);

        // Combine effects
        vec3 color = vec3(staticNoise);
        color *= scanLine;
        color *= vignette;

        // Add color tint
        color *= vec3(0.95, 0.97, 0.93);

        gl_FragColor = vec4(color, 1.0);
      }
    `;

    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    gl.shaderSource(vertexShader, vertexShaderSource);
    gl.compileShader(vertexShader);

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(fragmentShader, fragmentShaderSource);
    gl.compileShader(fragmentShader);

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.useProgram(program);

    const positions = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);

    const texCoords = new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]);

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    const texCoordBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(program, "a_position");
    const texCoordLocation = gl.getAttribLocation(program, "a_texCoord");

    const resolutionLocation = gl.getUniformLocation(program, "u_resolution");
    const timeLocation = gl.getUniformLocation(program, "u_time");

    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    let startTime = Date.now();

    const render = () => {
      gl.clearColor(0, 0, 0, 0);

      // Enable alpha blending
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.clear(gl.COLOR_BUFFER_BIT);

      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      gl.uniform1f(timeLocation, (Date.now() - startTime) / 1000);

      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
      gl.enableVertexAttribArray(texCoordLocation);
      gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 0, 0);

      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      this.animationId = requestAnimationFrame(render);
    };

    this.staticTimeout = setTimeout(() => {
      resizeCanvas();
      render();
    }, 100);
  },

  destroyed() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.staticTimeout) {
      clearTimeout(this.staticTimeout);
    }
  },
};
